import React from "react";
import _ from "lodash";

import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";

import RiskProfileSlider from "../../../FinalStep/components/RiskProfileSlider";
import SliderFormElementV2 from "../../../../formElement/SliderFormElementV2";
import InfoCard from "../../../DynamicStep/components/InfoCard";
import RiskProfileCategoryCard from "../../../FinalStep/components/RiskProfileCategoryCard";
import commonStyles from "../../../styles";
import {getSRRICategory} from "../../../FinalStep/FinalStep";
import {withPercentOrDash} from "../../../../../../../../../utils/utils";
import {DisabledReasonTooltip} from "../../../../../../../../../components/Tooltip";

import useStyles from "./styles";

const useCommonStyles = makeStyles(commonStyles);

const calcTotalMins = (data) => Object.values(data).reduce((sum, {value: [min]}) => sum + min, 0);
const calcMaxAllowed = (minimumsTotal, value) => 100 - minimumsTotal + value[0];
const getMaxBySRI = (q, sri) => _.get(q.max_config, sri, 100)
const setNewMaxValue = (answer, maxAllowed) => {
  const {initialMax, value} = answer;

  if (value[1] > maxAllowed) { // auto decrease
    value[1] = maxAllowed;
  } else if (value[1] < initialMax){ // auto increase
    value[1] = _.min([initialMax, maxAllowed])
  }
}
const setValuesIfMaxExceeds = (newLimits, currentField, limitsSRIConfigs, sri) => {
  const newMinimumsTotal = calcTotalMins(newLimits);

  Object.keys(newLimits).forEach((field) => {
    if(field !== currentField) {
      const answer = newLimits[field];

      setNewMaxValue(
        answer,
        _.min([calcMaxAllowed(newMinimumsTotal, answer.value), getMaxBySRI(limitsSRIConfigs[field], sri)])
      )
    }
  })
}

const InvestmentLimitsStep = (props) => {
  const {
    questions: [sriQuestion, limitsQuestion],
    onAnswerChange,
    dataService
  } = props;

  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const sri = sriQuestion.answer;
  const assetManagementConfig = React.useMemo(() => {
    return dataService.getBankConfig()['individual_asset_managment_config'];
  }, [dataService]);

  const limitsMapByUID = React.useMemo(() => {
    return _.keyBy(assetManagementConfig.questions, 'uid')
  }, [assetManagementConfig])

  const sriCategory = React.useMemo(() => getSRRICategory(sri), [sri]);

  if(!sri){
    onAnswerChange(sriQuestion.uid, dataService._srri)

    return null;
  }

  const SRIConfig = assetManagementConfig.sri_config[sri];

  if(_.isEmpty(limitsQuestion.answer)){
    // init answers structure
    const setInitial = (q, answerData) => {
      const initialMax = getMaxBySRI(q, sri);
      const answer = {value: [0, initialMax], initialMax: initialMax};

      if(q.sub_questions){
        answer.sub_limits = {};
        q.sub_questions.forEach(sub_q => setInitial(sub_q, answer.sub_limits))
      }
      answerData[q.uid] = answer;
    }

    const initialAnswer = {};
    assetManagementConfig.questions.forEach(q => setInitial(q, initialAnswer))

    onAnswerChange(limitsQuestion.uid, initialAnswer)

    return null;
  }

  const changeSRI = (newSRI) => {
    const newLimits = {};

    const setQuestionMax = (q, parentAnswer, sub=false) => {
      const answer = {...parentAnswer[q.uid]};
      const maxAllowed = getMaxBySRI(q, newSRI);

      setNewMaxValue(answer, maxAllowed)

      if (q.sub_questions) {
        q.sub_questions.forEach(sub_q => setQuestionMax(sub_q, answer.sub_limits, true))
      }

      if(!sub){
        newLimits[q.uid] = answer;
      }
    }

    assetManagementConfig.questions.forEach(q => setQuestionMax(q, limitsQuestion.answer))

    onAnswerChange(limitsQuestion.uid, newLimits)

    sriQuestion.error = null;
    onAnswerChange(sriQuestion.uid, newSRI)
  }

  const totalMins = calcTotalMins(limitsQuestion.answer);


  return (
    <Grid container spacing={4}>
      {/* empty line just for spacing */}
      <Grid item xs={12}>&nbsp;</Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} className={classes.subheader}>Risikoprofil der Strategie auf Basis der Kundenangaben</Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md>
                <InfoCard uid={'sri-info'} isNewDesign>
                  {SRIConfig.info}
                </InfoCard>
              </Grid>
              <Grid item xs={12} md={"auto"}>
                <div className={commonClasses.riskProfileCard}>
                  <Grid container spacing={2}>
                    <RiskProfileCategoryCard category={sriCategory} />
                  </Grid>
                </div>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} data-id={'sri-slider-container'} style={{marginTop: 64, marginLeft: 30, marginRight: 30, height: 90, position: 'relative'}}>
            <RiskProfileSlider sri={sri} maxSRI={_.max([dataService._srri, sri])} onChange={changeSRI} />
          </Grid>
          {sriQuestion.error && <Grid item xs={12}><b style={{color: 'red'}}>{sriQuestion.error}</b></Grid>}
          <Grid item xs={12}>
            <Grid container spacing={2} justify={"space-between"} className={classes.finIndicators} data-id={'finIndicators-container'}>
              <Grid item>
                <div>MAX. VERLUST</div>
                <div className={classes.finIndicatorNumber}>
                  <b>{withPercentOrDash(SRIConfig.max_loss, false)}</b>
                </div>
              </Grid>
              <Grid item>
                <div>MAX. VOLATILITÄT P.A. (5 JAHRE)</div>
                <div className={classes.finIndicatorNumber}>
                  <b>{withPercentOrDash(SRIConfig.max_vola_pa, false)}</b>
                </div>
              </Grid>
              <Grid item>
                <div>ZIELRENDITE P.A.</div>
                <div className={classes.finIndicatorNumber}>
                  <b>{withPercentOrDash(SRIConfig.min_target_return, false)}</b>
                </div>
              </Grid>
              <Grid item lg={4}>
                <div>BENCHMARK</div>
                <div><b>{SRIConfig.benchmark}</b></div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} className={classes.subheader}>Anlagegrenzen</Grid>
          <Grid item xs={12}>
            <InfoCard uid={'limits'} isNewDesign>
              Die Maximalgrenzen sind dynamisch: Die Obergrenze einer Kategorie entspricht einer von der Risikoklasse
              abhängigen Begrenzung bzw. 100% abzüglich der Summe der Mindestquoten aller anderen Kategorien.
            </InfoCard>
          </Grid>
          <Grid item xs={12}>
            {assetManagementConfig.questions.map(q => (
              <Grid key={q.uid} container spacing={1} alignItems={"center"} data-id={`limits-${q.uid}-container`}>
                <Grid item xs={3} lg={2}><b>{q.question_text}</b></Grid>
                <DisabledReasonTooltip
                  title={q.disabled && q.disabledReason}
                  width={600}
                  placement={'top'}
                  classes={{tooltip: classes.disabledReasonTooltip}}
                >
                  <Grid item xs={9} lg={10}>
                    <div className={classes.sliderContainer} data-id={`limits-${q.uid}-slider-container`}>
                      <RangeLimitSlider
                        value={limitsQuestion.answer[q.uid].value}
                        disabled={q.disabled}
                        onChange={(value) => {
                          const newLimits = {
                            ...limitsQuestion.answer,
                            [q.uid]: {
                              ...limitsQuestion.answer[q.uid],
                              value,
                              initialMax: value[1]
                            }
                          };

                          // if min changed - recalculate values based on new max
                          if(value[0] !== limitsQuestion.answer[q.uid].value[0]){
                            setValuesIfMaxExceeds(newLimits, q.uid, limitsMapByUID, sri);
                          }

                          onAnswerChange(limitsQuestion.uid, newLimits)
                        }}
                        maxBySRI={getMaxBySRI(q, sri)}
                        totalMins={totalMins}
                      />
                    </div>
                  </Grid>
                </DisabledReasonTooltip>
                {(q.sub_questions || []).map(sub_q => (
                  <Grid item xs={12} key={sub_q.uid} data-id={`sub-limits-${sub_q.uid}-container`}>
                    <Grid container spacing={1} alignItems={"center"} justify={"center"}>
                      <Grid item xs={3} lg={2}><i>{sub_q.question_text}</i></Grid>
                      <Grid item xs={9} lg={8}>
                        <div className={classes.sliderContainer} data-id={`sub-limits-${sub_q.uid}-slider-container`}>
                          <RangeLimitSlider
                            value={limitsQuestion.answer[q.uid].sub_limits[sub_q.uid].value}
                            onChange={(value) => {
                              onAnswerChange(limitsQuestion.uid, {
                                ...limitsQuestion.answer,
                                [q.uid]: {
                                  ...limitsQuestion.answer[q.uid],
                                  sub_limits: {
                                    ...limitsQuestion.answer[q.uid].sub_limits,
                                    [sub_q.uid]: {value, initialMax: value[1]}
                                  }
                                }
                              })
                            }}
                            maxBySRI={getMaxBySRI(sub_q, sri)}
                            subLimit
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default InvestmentLimitsStep;

const LIMIT_RANGE_MARKS = [
  {value: 0, label: '0%'},
  {value: 5, label: ''},
  {value: 10, label: ''},
  {value: 15, label: ''},
  {value: 20, label: ''},
  {value: 25, label: '25%'},
  {value: 30, label: ''},
  {value: 35, label: ''},
  {value: 40, label: ''},
  {value: 45, label: ''},
  {value: 50, label: '50%'},
  {value: 55, label: ''},
  {value: 60, label: ''},
  {value: 65, label: ''},
  {value: 70, label: ''},
  {value: 75, label: '75%'},
  {value: 80, label: ''},
  {value: 85, label: ''},
  {value: 90, label: ''},
  {value: 95, label: ''},
  {value: 100, label: '100%'}
]

const RangeLimitSlider = ({value, disabled, onChange, maxBySRI=100, totalMins, subLimit=false}) => {
  const max = (
    subLimit
      ? maxBySRI
      : _.min([maxBySRI, calcMaxAllowed(totalMins, value)])
    ) || 1;  // for max=0 to prevent width=0

  const labelFormatter = React.useCallback((val) => {
    return subLimit ? <span style={{fontSize: 12}}>{val}%</span> : `${val}%`
  }, [subLimit])

  return (
    <SliderFormElementV2
      value={value}
      disabled={disabled}
      label={labelFormatter}
      marks={LIMIT_RANGE_MARKS}
      onChange={onChange}
      step={5}
      max={max}
      style={{
        position: 'absolute',
        left: 0,
        right: `${100 - max}%`,
        top: 0,
        width: 'auto'
      }}
    />
  )
}